import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Heart, Quote, ArrowRight } from "lucide-react";

export default function StoriesPage() {
  const stories = [
    {
      quote: "When my mother was hospitalized 200 miles from home, I didn't know how I'd afford to stay nearby. Hope Hospitality gave me a comfortable place to rest so I could be there for her every day. The hotel staff were so welcoming, and knowing I didn't have to worry about costs let me focus entirely on being there for Mom during her recovery.",
      name: "Sarah M.",
      relation: "Daughter",
      location: "Dallas, TX",
      image: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=200&h=200&fit=crop&crop=face",
      duration: "2 weeks",
      year: "2024"
    },
    {
      quote: "Our son was in the NICU for three weeks after being born premature. Being able to stay close by instead of driving 4 hours each way made all the difference. We could be there for every feeding, every doctor's update. Hope Hospitality didn't just give us a room—they gave us peace of mind during the scariest time of our lives.",
      name: "The Rodriguez Family",
      relation: "Parents",
      location: "Phoenix, AZ",
      image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=200&h=200&fit=crop&crop=face",
      duration: "3 weeks",
      year: "2024"
    },
    {
      quote: "After my husband's accident, I was lost. I'd never been to the city before, didn't know anyone, and had no idea where to stay. Hope Hospitality not only gave me a place to stay but connected me with transportation to the hospital every day. The volunteers checked in on me, made sure I was eating. They were angels when I needed them most.",
      name: "Margaret T.",
      relation: "Wife",
      location: "Denver, CO",
      image: "https://images.unsplash.com/photo-1580489944761-15a19d654956?w=200&h=200&fit=crop&crop=face",
      duration: "10 days",
      year: "2023"
    },
    {
      quote: "My father needed specialized treatment that wasn't available in our small town. The thought of paying for a hotel for potentially weeks was overwhelming on top of medical bills. Hope Hospitality stepped in and took that burden off our shoulders. I could focus on supporting Dad instead of worrying about money.",
      name: "James L.",
      relation: "Son",
      location: "Seattle, WA",
      image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=200&h=200&fit=crop&crop=face",
      duration: "4 weeks",
      year: "2024"
    },
    {
      quote: "When my daughter was diagnosed and needed immediate surgery, we dropped everything and drove six hours to the children's hospital. We had nothing planned, nowhere to stay. Hope Hospitality found us a room within hours. The kindness of strangers during our darkest moment restored our faith in humanity.",
      name: "Linda & Robert K.",
      relation: "Parents",
      location: "Austin, TX",
      image: "https://images.unsplash.com/photo-1499952127939-9bbf5af6c51c?w=200&h=200&fit=crop&crop=face",
      duration: "1 week",
      year: "2024"
    },
    {
      quote: "As a single mom with two other kids at home, I couldn't afford to take unpaid time off AND pay for a hotel when my youngest needed heart surgery. Hope Hospitality covered my lodging, and local volunteers even helped with groceries. I don't know what I would have done without them.",
      name: "Tanya W.",
      relation: "Mother",
      location: "Portland, OR",
      image: "https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=200&h=200&fit=crop&crop=face",
      duration: "12 days",
      year: "2023"
    }
  ];

  return (
    <div className="relative">
      {/* Hero Section */}
      <section className="relative py-24 lg:py-32 overflow-hidden bg-secondary">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-3xl space-y-6 animate-fade-in-up">
            <span className="text-accent font-medium text-sm uppercase tracking-wider">Stories of Hope</span>
            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold text-foreground leading-tight">
              The Families We <span className="text-primary">Aim to Help</span>
            </h1>
            <p className="text-lg text-muted-foreground leading-relaxed">
              These stories represent the types of families Hope Hospitality is being built to serve. 
              With your support as a partner or donor, we can turn these scenarios into real success stories.
            </p>
          </div>
        </div>
      </section>

      {/* Goals Banner */}
      <section className="py-12 bg-primary">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-6">
            <span className="text-primary-foreground/80 text-sm uppercase tracking-wider">Our Goals</span>
          </div>
          <div className="flex flex-wrap justify-center gap-12 text-primary-foreground text-center">
            <div>
              <div className="font-serif text-3xl font-bold">500</div>
              <div className="text-primary-foreground/80 text-sm">Families to Serve</div>
            </div>
            <div>
              <div className="font-serif text-3xl font-bold">2,000</div>
              <div className="text-primary-foreground/80 text-sm">Nights to Provide</div>
            </div>
            <div>
              <div className="font-serif text-3xl font-bold">$150k</div>
              <div className="text-primary-foreground/80 text-sm">In Family Savings</div>
            </div>
          </div>
          <p className="text-center text-primary-foreground/70 mt-6 text-sm">
            Help us reach these goals and create more stories of hope.
          </p>
        </div>
      </section>

      {/* Stories Grid */}
      <section className="py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {stories.map((story, index) => (
              <Card 
                key={index} 
                className="bg-card border-border/50 card-hover overflow-hidden"
                data-testid={`story-card-${index}`}
              >
                <CardContent className="p-0">
                  <div className="flex flex-col md:flex-row">
                    <div className="md:w-1/3 p-6 bg-secondary/50 flex flex-col items-center justify-center text-center">
                      <img 
                        src={story.image} 
                        alt={story.name}
                        className="w-24 h-24 rounded-full object-cover mb-4"
                      />
                      <div className="font-serif font-bold text-foreground">{story.name}</div>
                      <div className="text-sm text-muted-foreground">{story.relation}</div>
                      <div className="text-sm text-muted-foreground">{story.location}</div>
                      <div className="mt-3 px-3 py-1 bg-primary/10 rounded-full text-xs text-primary font-medium">
                        {story.duration} stay • {story.year}
                      </div>
                    </div>
                    <div className="md:w-2/3 p-8 flex flex-col justify-center">
                      <Quote className="w-8 h-8 text-accent/30 mb-4" />
                      <blockquote className="text-muted-foreground leading-relaxed italic">
                        "{story.quote}"
                      </blockquote>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Share Your Story CTA */}
      <section className="py-24 lg:py-32 bg-secondary">
        <div className="max-w-3xl mx-auto px-6 lg:px-12 text-center">
          <Quote className="w-12 h-12 text-primary mx-auto mb-6" />
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-foreground">
            Share Your Story
          </h2>
          <p className="text-muted-foreground mt-4 text-lg leading-relaxed">
            Has Hope Hospitality helped your family? We'd love to hear from you. 
            Your story can inspire others and show donors the real impact of their generosity.
          </p>
          <Button 
            className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground mt-8 h-12 px-8"
            data-testid="share-story-button"
            onClick={() => window.location.href = 'mailto:stories@hopehospitality.org?subject=My Hope Hospitality Story'}
          >
            Share Your Story
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </section>

      {/* Donate CTA */}
      <section className="py-24 lg:py-32 bg-accent/10">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <Heart className="w-16 h-16 text-accent mx-auto mb-6" />
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-foreground">
            Help Write More Stories of Hope
          </h2>
          <p className="text-muted-foreground mt-4 text-lg leading-relaxed max-w-2xl mx-auto">
            Every donation helps another family stay close to their loved one during 
            a hospital stay. Be part of their story.
          </p>
          <Link to="/donate">
            <Button 
              size="lg"
              className="rounded-full bg-accent hover:bg-accent/90 text-accent-foreground mt-8 h-14 px-10 btn-hover"
              data-testid="stories-donate-button"
            >
              <Heart className="w-5 h-5 mr-2" />
              Make a Donation
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
